import { getBrandNameById, getCanonicalPagePath } from "@platformx/utilities";

export type StoredCanonical = { path: string; slug: string };

/**
 * The canonical path a save (and the edit screen) should use (KIWI-6401). The stored value is the
 * source of truth: it is reused as-is until the editor changes the slug, and only then rebuilt —
 * from the document's own primary brand (the dropdown brand is just the fallback for a brand-new
 * document that has no brand yet).
 */
export const resolveCanonicalPath = ({
  slug,
  language,
  tagName,
  brandId = "",
  stored,
}: {
  slug: string;
  language: string;
  tagName: string;
  brandId?: string;
  stored?: StoredCanonical | null;
}): string =>
  stored?.path && stored.slug === slug
    ? stored.path
    : getCanonicalPagePath(slug, language, tagName, getBrandNameById(brandId));

/**
 * The brand-related fields to carry from a fetched document into an editor's state:
 *  - brand_name: the primary brand (read as responseBrandname) — saves keep it instead of
 *    replacing it with the dropdown brand;
 *  - secondary_brandname: only when the document really has shared brands, so an edit does not wipe
 *    them (the update replaces the whole document);
 *  - storedCanonical: the canonical the document was loaded with.
 */
export const brandFieldsFromDoc = (doc: any) => ({
  ...(doc?.responseBrandname || doc?.brand_name
    ? { brand_name: doc.responseBrandname || doc.brand_name }
    : {}),
  ...(Array.isArray(doc?.secondary_brandname) && doc.secondary_brandname.length > 0
    ? { secondary_brandname: doc.secondary_brandname }
    : {}),
  ...(doc?.canonical_url
    ? { storedCanonical: { path: doc.canonical_url, slug: doc.slug || "" } }
    : {}),
});

/** What a save should send for the brand fields, from an editor state that carries them. */
export const brandFieldsToSave = (state: any) => ({
  ...(state?.brand_name ? { brand_name: state.brand_name } : {}),
  ...(Array.isArray(state?.secondary_brandname) && state.secondary_brandname.length > 0
    ? { secondary_brandname: state.secondary_brandname }
    : {}),
});
